import React, { useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { toast, Toaster } from 'sonner';
import Navbar from './components/site/Navbar';
import Hero from './components/site/Hero';
import PlatformSteps from './components/site/PlatformSteps';
import PlatformFeatures from './components/site/PlatformFeatures';
import { TrustedBrands, EarlyBuilders, FounderNote } from './components/site/Social';
import Resources from './components/site/Resources';
import Pricing from './components/site/Pricing';
import { FinalCTA, Footer } from './components/site/FooterCTA';
import Admin from './pages/Admin';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import PublicPage from './pages/PublicPage';
import { Login, Signup } from './pages/Auth';
import ResetPassword from './pages/ResetPassword';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import { AuthProvider } from './lib/AuthContext';
import { SubscriptionProvider, useSubscription } from './lib/SubscriptionContext';
import './App.css';

// When the user arrives at `/#pricing` (etc.) from another route via
// the header nav, browsers don't auto-scroll because the anchor element
// wasn't in the initial static HTML — React renders it a beat later.
// Rerun the scroll after each navigation-with-hash so the section
// smoothly lands in view. Kept out of Navbar so any deep link (e.g.
// footer, external, share) benefits equally.
const ScrollToHash = () => {
  const { hash, pathname } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const id = hash.slice(1);
    // Two rAFs give the freshly mounted route enough time to paint
    // the target section before we try to scroll to it.
    const tick = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
    return () => cancelAnimationFrame(tick);
  }, [hash, pathname]);
  return null;
};

// When a user returns from Stripe checkout, whichever page the Stripe
// Payment Link redirects to should carry `?checkout=success` (optional
// `?plan=pro`) in the URL. This handler runs on every route, so it
// catches the marker wherever the redirect lands (Projects,
// ProjectDetail, Landing, etc.), forces a fresh entitlement read from
// Stripe, shows a clear success toast, and cleans the marker out of
// the URL so a manual refresh doesn't retoast.
//
// Falls back gracefully: if refresh() fails (rare — Stripe transient
// blip), we still show a "processing" toast so the user isn't left
// wondering whether the payment worked.
const CheckoutSuccessHandler = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh } = useSubscription();
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) return;
    const params = new URLSearchParams(location.search);
    if (params.get('checkout') !== 'success') return;
    handledRef.current = true;

    const planHint = (params.get('plan') || '').toLowerCase();

    // Optimistic toast first — the fetch may take a beat.
    const optimisticPlan = planHint === 'enterprise' ? 'Enterprise'
                         : planHint === 'pro' ? 'Pro'
                         : 'your paid plan';
    toast.success(`You're on ${optimisticPlan} — publishing unlocked`, {
      description: 'Confirming with Stripe…',
      duration: 6000,
    });

    (async () => {
      const next = await refresh();
      const planLabel = next?.plan === 'enterprise' ? 'Enterprise'
                      : next?.plan === 'pro' ? 'Pro'
                      : null;
      if (planLabel) {
        toast.success(`You're on ${planLabel} — publishing unlocked`, {
          description: 'Head to your projects and hit Publish.',
          duration: 8000,
        });
      } else {
        // Stripe hasn't propagated yet — reassure, don't alarm.
        toast.message('Payment received — finalising your upgrade', {
          description: 'This can take a few seconds. Refresh in a moment if the badge still shows Free.',
          duration: 8000,
        });
      }
    })();

    // Strip the query params so the URL is clean and a refresh
    // doesn't re-fire the toast.
    params.delete('checkout');
    params.delete('plan');
    const q = params.toString();
    navigate(
      { pathname: location.pathname, search: q ? `?${q}` : '', hash: location.hash },
      { replace: true }
    );
  }, [location.search, location.pathname, location.hash, navigate, refresh]);

  return null;
};

const Landing = () => (
  <div className="min-h-screen font-sans antialiased" style={{ backgroundColor: 'var(--yb-bg)', color: 'var(--yb-text)' }}>
    <Navbar />
    <main>
      <Hero />
      <PlatformSteps />
      <PlatformFeatures />
      <TrustedBrands />
      <FounderNote />
      <EarlyBuilders />
      <Resources />
      <Pricing />
      <FinalCTA />
    </main>
    <Footer />
  </div>
);

function App() {
  return (
    <AuthProvider>
      <SubscriptionProvider>
        <BrowserRouter>
          <ScrollToHash />
          <CheckoutSuccessHandler />
          <Toaster position="top-right" richColors closeButton />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/projects/:id" element={<ProjectDetail />} />
            {/*
              Stripe Payment Link redirects post-checkout to `/workspace`
              (no matching SPA route → user saw a blank page). Alias it
              to /projects with the `checkout=success` marker so the
              app-level CheckoutSuccessHandler fires the entitlement
              refresh + Pro toast on landing. `replace` keeps the back
              button clean (users don't need to revisit /workspace).
            */}
            <Route
              path="/workspace"
              element={<Navigate to="/projects?checkout=success&plan=pro" replace />}
            />
            <Route path="/p/:id" element={<PublicPage />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
          </Routes>
        </BrowserRouter>
      </SubscriptionProvider>
    </AuthProvider>
  );
}

export default App;
